import type {
  ColabortorDayType,
  CenterHours
} from '#shared/types/appTypes/rhPresencesTable/homeTable'

export type HoursResult = {
  'extraHours': number
  'usualHours': number
}

/**
 * Extract center-specific hours from a day's data
 * @param day - The day data containing centersHours
 * @param centerCodes - The center codes to filter by, or empty array for total hours
 * @returns Object with usualHours and extraHours (summed across the selected centers)
 */
export function getCenterHours(
  day: { 'centersHours': CenterHours[]; 'extraHours': number | '-'; 'usualHours': number | '-' },
  centerCodes: string[]
): HoursResult {
  if (!centerCodes || centerCodes.length === 0) {
    return {
      'extraHours': typeof day.extraHours === 'number' ? day.extraHours : 0,
      'usualHours': typeof day.usualHours === 'number' ? day.usualHours : 0
    }
  }

  return day.centersHours
    .filter((ch: CenterHours) => centerCodes.includes(ch.code))
    .reduce(
      (acc: HoursResult, ch: CenterHours) => ({
        'extraHours': acc.extraHours + (ch.extraHours ?? 0),
        'usualHours': acc.usualHours + (ch.usualHours ?? 0)
      }),
      { 'extraHours': 0, 'usualHours': 0 }
    )
}

/**
 * Get the selected center codes that have hours on a given day
 * @param day - The day data containing centersHours
 * @param centerCodes - The selected center codes
 * @returns Array of center codes present in the day
 */
export function getMatchedCenterCodes(
  day: { 'centersHours': CenterHours[] },
  centerCodes: string[]
): string[] {
  if (!centerCodes || centerCodes.length === 0) return []
  return day.centersHours
    .filter((ch: CenterHours) => centerCodes.includes(ch.code))
    .map((ch: CenterHours) => ch.code)
}

/**
 * Check if a day matches the column filters (center and/or issue)
 * @param day - The day data to check
 * @param filterByCenters - Center codes filter, or empty array
 * @param filterByIssue - Issue/fault filter, or null
 * @returns true if the day matches all active filters
 */
export function checkIfDayMatchesFilter(
  day: { 'centersHours': CenterHours[]; 'faults'?: string },
  filterByCenters: string[],
  filterByIssue: string | null
): boolean {
  let matches = true

  if (filterByCenters && filterByCenters.length > 0) {
    matches =
      matches && day.centersHours.some((ch: CenterHours) => filterByCenters.includes(ch.code))
  }

  if (filterByIssue) {
    matches = matches && day.faults === filterByIssue
  }

  return matches
}
